import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  listUserLiveSessions,
  LiveReportedSessionSummary,
} from '../services/liveAccessApi';

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const LiveAccessUserLivestreams: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<LiveReportedSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await listUserLiveSessions(userId, { page: 1, limit: 50 });
      setSessions(data.sessions);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load livestreams');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const host = sessions[0]?.host;
  const totalReports = useMemo(
    () => sessions.reduce((sum, session) => sum + session.reportCount, 0),
    [sessions],
  );
  const reportedSessions = useMemo(
    () => sessions.filter((session) => session.reportCount > 0).length,
    [sessions],
  );

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />
      <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <button
          onClick={() => navigate(`/live-access/${userId}`)}
          className='mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors'
        >
          <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
          Back to Application Detail
        </button>

        <div className='mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
            <div>
              <h1 className='text-2xl font-bold text-gray-900'>
                {host?.Name || host?.UserName || userId || 'User'} Livestreams
              </h1>
              <p className='mt-1 text-sm text-gray-500'>
                Review all livestream sessions by this applicant and drill into report details.
              </p>
              <p className='mt-1 text-xs font-mono text-gray-400'>{userId}</p>
            </div>
            <button
              onClick={() => navigate(`/live-access/${userId}/profile`)}
              className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50'
            >
              View Full Profile
            </button>
          </div>
        </div>

        {!loading && (
          <div className='mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3'>
            <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
              <p className='text-sm font-medium text-gray-500'>Total Sessions</p>
              <p className='mt-2 text-3xl font-bold text-gray-900'>{sessions.length}</p>
            </div>
            <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
              <p className='text-sm font-medium text-gray-500'>Reported Sessions</p>
              <p className='mt-2 text-3xl font-bold text-amber-600'>{reportedSessions}</p>
            </div>
            <div className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm'>
              <p className='text-sm font-medium text-gray-500'>Total Live Reports</p>
              <p className='mt-2 text-3xl font-bold text-rose-600'>{totalReports}</p>
            </div>
          </div>
        )}

        <div className='rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden'>
          {loading ? (
            <div className='flex items-center justify-center py-20'>
              <div className='h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[#FF9C15]' />
            </div>
          ) : sessions.length === 0 ? (
            <div className='px-6 py-16 text-center'>
              <p className='text-lg font-semibold text-gray-900'>No livestreams found</p>
              <p className='mt-2 text-sm text-gray-500'>This user does not have any livestream sessions yet.</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>Session</th>
                    <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>Status</th>
                    <th className='px-6 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-500'>Reports</th>
                    <th className='px-6 py-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-500'>Reporters</th>
                    <th className='px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>Last Reported</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-200'>
                  {sessions.map((session) => (
                    <tr
                      key={session.sessionId}
                      className='cursor-pointer hover:bg-gray-50'
                      onClick={() =>
                        navigate(`/live-access/${encodeURIComponent(userId || '')}/livestreams/${encodeURIComponent(session.sessionId)}`)
                      }
                    >
                      <td className='px-6 py-4'>
                        <div className='flex items-center gap-3'>
                          {session.coverUrl ? (
                            <img src={session.coverUrl} alt={session.sessionTitle || session.sessionId} className='h-14 w-14 rounded-lg object-cover' />
                          ) : (
                            <div className='flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-to-br from-[#FF9C15] to-[#F42026] text-sm font-bold text-white'>
                              LIVE
                            </div>
                          )}
                          <div className='min-w-0'>
                            <p className='truncate text-sm font-semibold text-gray-900'>{session.sessionTitle || 'Untitled Livestream'}</p>
                            <p className='mt-1 truncate text-xs font-mono text-gray-400'>{session.sessionId}</p>
                          </div>
                        </div>
                      </td>
                      <td className='px-6 py-4'>
                        <span className='inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700'>
                          {session.sessionLifecycleStatus}
                        </span>
                      </td>
                      <td className='px-6 py-4 text-center text-sm font-semibold text-rose-600'>{session.reportCount}</td>
                      <td className='px-6 py-4 text-center text-sm font-semibold text-blue-600'>{session.reporterCount}</td>
                      <td className='px-6 py-4 text-sm text-gray-600'>{formatDate(session.lastReportedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveAccessUserLivestreams;
